import { connect } from 'react-redux';
import { getGuideChapterStack } from 'features/guide/chapter/actions';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { setTopNavColorLight } from 'features/navigation/header/actions';
import { darkTheme, palette } from 'theme';
import styled from 'styled-components';
import loadable from '@loadable/component';
import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import { MuiThemeProvider } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Container from 'components/Container';
import ContentTypography from 'components/ContentTypography';
import PaddedDivider from 'components/PaddedDivider';
import { PageHeaderGrid } from 'components/PageHeader';
import { ResponsiveBackgroundImg } from 'components/ResponsiveImg';

import TableOfContentsIcon from 'mdi-material-ui/TableOfContents';

import MetaTags from 'components/MetaTags';
import SwiftypeTags from 'components/SwiftypeTags';

const InlineResourceCTA = loadable(() => import('components/InlineCTAs'), {
  resolveComponent: components => components.InlineResourceCTA
});
const InlineBlogCTA = loadable(() => import('components/InlineCTAs'), {
  resolveComponent: components => components.InlineBlogCTA
});
const InlineCTA = loadable(() => import('components/InlineCTAs'), {
  resolveComponent: components => components.InlineCTA
});
const PhotoCTA = loadable(() => import('components/CTACards'), {
  resolveComponent: components => components.PhotoCTA
});
const BoldCTA = loadable(() => import('components/CTACards'), {
  resolveComponent: components => components.BoldCTA
});
const RelatedContent = loadable(() => import('components/RelatedContent'), {
  resolveComponent: components => components.RelatedContent
});
const RelatedContentWrapper = loadable(
  () => import('components/RelatedContent'),
  {
    resolveComponent: components => components.RelatedContentWrapper
  }
);
const TOC = loadable(() => import('../elements/TOC'));
const GuidePagination = loadable(() => import('../elements/GuidePagination'));
const GuideFooter = loadable(() => import('../elements/GuideFooter'));

const Hero = styled.div`
  padding-top: 64px;
  display: flex;
  align-items: center;
  position: relative;
  margin-top: -64px;
  margin-bottom: 40px;
  background: ${palette.grey[900]};
  position: relative;
  z-index: 1;
`;

const HeroContainer = styled(Container)`
  position: relative;
  z-index: 1;
`;

const GradientBackgroundImg = styled(ResponsiveBackgroundImg)`
  background: ${props =>
    !props.color1 || !props.color2
      ? palette.grey[900]
      : `linear-gradient(45deg, ${props.color1} 0%, ${props.color2} 100%)`};
`;

const LeadTypography = styled(ContentTypography)`
  font-size: 1.15em;
  font-weight: 300;
`;

const ChapterTypography = styled(Typography)`
  margin-bottom: 2em;
  font-size: 1em;
`;

const SlimPageHeaderGrid = styled(PageHeaderGrid)`
  margin-top: 2vh;
  margin-bottom: 2vh;
`;

class GuideChapter extends React.PureComponent {
  componentWillMount() {
    this.props.setTopNavColorLight();
  }

  componentDidMount() {
    this.props.getGuideChapterStack(this.props.location.pathname);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.props.getGuideChapterStack(this.props.location.pathname);
    }
  }

  getCurrentChapterNumber(chapters, currentUID) {
    let chapterIndex = chapters.findIndex(
      chapter => chapter.uid === currentUID
    );
    return String(chapterIndex + 1).padStart(2, 0);
  }

  render() {
    const { stack, loading } = this.props;

    return !loading ? (
      <Fragment>
        <MetaTags stack={stack} />
        <SwiftypeTags
          stack={stack}
          title={stack.title}
          sections={stack.content.map(
            block => block.section && block.section.title
          )}
          contentType="Guide"
        />
        <Hero>
          <MuiThemeProvider theme={darkTheme}>
            <HeroContainer>
              <SlimPageHeaderGrid container justify="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" component="span">
                    {stack.guide_home_reference[0].title}
                  </Typography>
                </Grid>
              </SlimPageHeaderGrid>
            </HeroContainer>
          </MuiThemeProvider>
          <GradientBackgroundImg
            src={stack.guide_home_reference[0].background_image.url}
            defaultWidth={1500}
            color1={stack.guide_home_reference[0].background_gradient.color_1}
            color2={stack.guide_home_reference[0].background_gradient.color_2}
          />
        </Hero>

        <Container>
          <Grid container justify="center">
            <Grid item md={8} xs={12}>
              <ChapterTypography variant="overline" component="span">
                Chapter{' '}
                {this.getCurrentChapterNumber(
                  stack.guide_home_reference[0].chapter_references,
                  stack.uid
                )}
              </ChapterTypography>
              <Typography variant="h2" component="h1" gutterBottom>
                {stack.title}
              </Typography>
              <div data-swiftype-name="body" data-swiftype-type="text">
                <LeadTypography
                  variant="body2"
                  dangerouslySetInnerHTML={{ __html: stack.summary }}
                />
                <PaddedDivider />
                {stack.content &&
                  stack.content.map((block, idx) => (
                    <PrerenderSafeLazyLoad key={idx}>
                      {block.lead && (
                        <Fragment>
                          <LeadTypography
                            variant="body2"
                            dangerouslySetInnerHTML={{
                              __html: block.lead.text
                            }}
                          />
                        </Fragment>
                      )}

                      {block.section && (
                        <Fragment>
                          <Typography variant="h3" component="h2">
                            {block.section.title}
                          </Typography>
                          <ContentTypography
                            variant="body2"
                            dangerouslySetInnerHTML={{
                              __html: block.section.body
                            }}
                          />
                        </Fragment>
                      )}

                      {block.resource_inline && (
                        <aside data-swiftype-index="false">
                          <InlineResourceCTA
                            title={block.resource_inline.title}
                            resource={
                              block.resource_inline.resource_reference[0]
                            }
                            size={block.resource_inline.size}
                            overflow
                          />{' '}
                        </aside>
                      )}

                      {block.blog_inline && (
                        <aside data-swiftype-index="false">
                          <InlineBlogCTA
                            title={block.blog_inline.title}
                            post={block.blog_inline.blog_reference[0]}
                            size={block.blog_inline.size}
                            overflow
                          />{' '}
                        </aside>
                      )}

                      {block.cta && (
                        <aside data-swiftype-index="false">
                          {_.get(
                            block.cta,
                            'cta_reference[0]._content_type_uid'
                          ) === 'card_cta' && (
                            <InlineCTA
                              size="wide"
                              {..._.get(block.cta, 'cta_reference[0]')}
                            />
                          )}

                          {_.get(
                            block.cta,
                            'cta_reference[0]._content_type_uid'
                          ) === 'photo_cta' && (
                            <PhotoCTA
                              {..._.get(block.cta, 'cta_reference[0]')}
                            />
                          )}

                          {_.get(
                            block.cta,
                            'cta_reference[0]._content_type_uid'
                          ) === 'bold_cta' && (
                            <BoldCTA
                              size="wide"
                              {..._.get(block.cta, 'cta_reference[0]')}
                            />
                          )}
                        </aside>
                      )}
                    </PrerenderSafeLazyLoad>
                  ))}
              </div>
            </Grid>
          </Grid>
          <PrerenderSafeLazyLoad>
            <PaddedDivider />
            <GuideFooter>
              <Grid container spacing={32}>
                <Grid item md={3} xs={12}>
                  <GuidePagination
                    chapters={stack.guide_home_reference[0].chapter_references}
                    currentUID={stack.uid}
                  />
                  <PaddedDivider />
                  <Button
                    component={Link}
                    to={stack.guide_home_reference[0].url}
                    fullWidth
                  >
                    <TableOfContentsIcon /> To Beginning
                  </Button>
                </Grid>
                <Grid item md={9} xs={12}>
                  <Card>
                    <CardContent>
                      <TOC
                        chapters={
                          stack.guide_home_reference[0].chapter_references
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </GuideFooter>
          </PrerenderSafeLazyLoad>
        </Container>

        {stack.guide_home_reference[0].related_references && (
          <PrerenderSafeLazyLoad>
            <RelatedContentWrapper
              backgroundcolor={palette.grey[50]}
              title={stack.guide_home_reference[0].related_references.title}
            >
              <RelatedContent
                resource={
                  stack.guide_home_reference[0].related_references
                    .resource_references
                }
                blog={
                  stack.guide_home_reference[0].related_references
                    .blog_references
                }
              />
            </RelatedContentWrapper>
          </PrerenderSafeLazyLoad>
        )}
      </Fragment>
    ) : null;
  }
}

GuideChapter.propTypes = {
  stack: PropTypes.shape({
    title: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    content: PropTypes.array.isRequired,
    guide_home_reference: PropTypes.array.isRequired
  }).isRequired
};

export default connect(
  state => ({
    stack: state.guideChapter.stack,
    loading: state.guideChapter.loading
  }),
  {
    getGuideChapterStack,
    setTopNavColorLight
  }
)(GuideChapter);
